import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { TableCell, TableRow } from '@mui/material';
import useQuery from 'hooks/apollo/useQuery';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { getErrors, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { get, sequence, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { useToast } from 'hooks/toasts/useToast';
import { useMutation } from 'hooks/apollo/useMutation';
import useSubscription from 'hooks/apollo/useSubscription';
import { useReactiveVar } from '@apollo/client';
import { isIncomeSchemaEditableVar } from '../../variables';
import { concatFailure, items, required, shape, unique } from '@totalsoft/pure-validations';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_LEASING_INCOME_SCHEMAS_WRITE } from 'constants/rightsCodes';
import FlexBox from 'components/common/FlexBox';
import MaxHeightTable from 'components/common/MaxHeightTable';
import IconButton from 'components/common/buttons/IconButton';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { ThemedWrapper } from 'components/common/SurfaceStyles';
import { RoundedHeaderTableRow, ThemedHeaderTableCell } from 'components/common/table/TableStyles';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';
import { LEASING_INCOME_SCHEMA_GROUPS } from 'features/leasingIncomeSchema/queries';
import { SAVE_LEASING_INCOME_SCHEMA_GROUPS } from 'features/leasingIncomeSchema/mutations';
import { LEASING_INCOME_SCHEMA_GROUPS_SAVED } from 'features/leasingIncomeSchema/subscriptions';
import LeasingIncomeSchemaXGroupComponent from './LeasingIncomeSchemaXGroupComponent';

const validator = concatFailure(
  unique(x => x.groupId || {}, 'LeasingIncomeSchema.Group'),
  items(
    shape({
      groupId: required
    })
  )
);
const LeasingIncomeSchemaXGroupsContainer = ({ id }) => {
  const { t } = useTranslation();
  const addToast = useToast();
  const canWrite = usePermission(ADMIN_LEASING_INCOME_SCHEMAS_WRITE);
  const isEditable = useReactiveVar(isIncomeSchemaEditableVar);

  const [pending, setPending] = useState(false);
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens([]);
  const [validation, validate] = useDirtyFieldValidation(validator);

  const { loading } = useQuery(LEASING_INCOME_SCHEMA_GROUPS, {
    variables: { id },
    skip: !id,
    onCompleted: data => {
      resetLens((data?.leasingIncomeSchemaXGroups || []).map(item => ({ ...item, key: crypto.randomUUID() })));
    }
  });

  const [saveLeasingIncomeSchemaXGroups] = useMutation(SAVE_LEASING_INCOME_SCHEMA_GROUPS, {
    onError: () => {
      setPending(false);
    }
  });

  const handleAdd = () => {
    const current = lens |> get;
    (lens |> set)([...current, { key: crypto.randomUUID(), id: null, schemaId: id }]);
  };

  const handleDelete = key => {
    const current = lens |> get;
    (lens |> set)(current.filter(item => item.key !== key));
  };

  const handleSave = () => {
    const data = lens |> get;
    if (!validate(data)) return;

    const sanitizedData = data.map(partner => {
      const sanitizedPartner = { ...partner };

      delete sanitizedPartner.key;
      delete sanitizedPartner.groupName;
      delete sanitizedPartner.parentGroupName;

      return sanitizedPartner;
    });

    setPending(true);
    saveLeasingIncomeSchemaXGroups({
      variables: { schemaId: id, groups: sanitizedData }
    });
  };

  const hasItems = (lens.length |> get) > 0;
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);
  const disabled = useMemo(() => !canWrite || pending || !isEditable, [canWrite, pending, isEditable]);

  useEffect(() => {
    validate(lens |> get, dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  useGlobalIsDirty(isDirty);

  useSubscription(LEASING_INCOME_SCHEMA_GROUPS_SAVED, {
    skip: !id,
    variables: { id },
    onData: ({ client, data }) => {
      const currentGroups = lens |> get;
      const newGroups = data?.data?.leasingIncomeSchemaXGroupsSaved?.groups || [];
      currentGroups.forEach(currentGroup => {
        const groupToUpdate = newGroups.find(newGroup => newGroup.groupId === currentGroup.groupId);
        if (groupToUpdate) {
          groupToUpdate.groupName = currentGroup.groupName;
          groupToUpdate.parentGroupName = currentGroup.parentGroupName;
        }
      });
      resetLens(newGroups);
      client.writeQuery({
        query: LEASING_INCOME_SCHEMA_GROUPS,
        variables: { id },
        data: { leasingIncomeSchemaXGroups: newGroups }
      });
      setPending(false);
      addToast(t('LeasingIncomeSchema.GroupsSaved'), 'success');
    }
  });

  return (
    <FlexBox direction='column' gap={1.5} mt={1.5}>
      <FlexBox justifyContent='end' gap={0.5} px={1}>
        <IconButton type='add' onClick={handleAdd} disabled={disabled} />
        <IconButton type='save' disabled={disabled || !isDirty} onClick={handleSave} />
      </FlexBox>
      <MaxHeightTable maxHeight={500}>
        <MaxHeightTable.Head>
          <RoundedHeaderTableRow>
            <ThemedHeaderTableCell>{t('LeasingIncomeSchema.Group')}</ThemedHeaderTableCell>
            <ThemedHeaderTableCell>{t('LeasingIncomeSchema.Parent')}</ThemedHeaderTableCell>
            <ThemedHeaderTableCell width='100px' />
          </RoundedHeaderTableRow>
        </MaxHeightTable.Head>
        <MaxHeightTable.Body>
          {loading && (
            <TableRow>
              <TableCell colSpan={3}>
                <WrappedFakeText lines={5} />
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            hasItems &&
            (lens |> sequence).map((itemLens, index) => (
              <LeasingIncomeSchemaXGroupComponent
                key={itemLens.key |> get}
                lens={itemLens}
                validation={validation[index]}
                onDelete={handleDelete}
                disabled={disabled}
              />
            ))}
          {!loading && !hasItems && (
            <TableRow>
              <TableCell colSpan={3} align='center'>
                {t('LeasingIncomeSchema.NoGroups')}
              </TableCell>
            </TableRow>
          )}
        </MaxHeightTable.Body>
      </MaxHeightTable>
      {getErrors(validation) && (
        <ThemedWrapper color='error' flexDirection='column'>
          {getErrors(validation)
            .split(',')
            .map(error => (
              <span key={error}>{error}</span>
            ))}
        </ThemedWrapper>
      )}
    </FlexBox>
  );
};

LeasingIncomeSchemaXGroupsContainer.propTypes = {
  id: PropTypes.number
};

export default LeasingIncomeSchemaXGroupsContainer;
